/**
 * "Wiedza" / "Insights" — the analyses list and the article page, both closing
 * with the mock-up's conversion block (that is the lead mechanism here).
 */
import { clear, el, fromTrustedHtml } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import type { RouteMatch } from "../core/routes.js";
import { applyPageSeo } from "../core/seo.js";
import { breadcrumbs, eyebrow, pill } from "../components/common.js";
import { insightCard } from "../components/cards.js";
import { newsletterStrip } from "../components/overlays.js";
import { notFoundView } from "./status.js";

export async function knowledgeListView(ctx: AppContext, match: RouteMatch): Promise<HTMLElement> {
	const copy = ctx.copy();
	const tag = match.query.tag;
	const articles = await ctx.gateway.articles(tag);

	applyPageSeo(ctx, {
		title: `${copy.nav.knowledge} — ${copy.brand.name}`,
		description: copy.knowledge.lead,
	});

	const nodes = articles.edges.map((edge) => edge.node);
	const tags = Array.from(new Set(nodes.flatMap((article) => article.tags))).sort((a, b) =>
		a.localeCompare(b, ctx.locale()),
	);

	const grid = el("div", { class: "mb-grid mb-grid--3" });
	for (const article of nodes) grid.appendChild(insightCard(ctx, article));

	const filters = el("div", { class: "mb-filters" });
	const allLabel = ctx.locale() === "en" ? "All" : "Wszystkie";
	filters.appendChild(
		el(
			"a",
			{ class: `mb-filter${tag ? "" : " is-active"}`, href: ctx.href("knowledge") },
			allLabel,
		),
	);
	for (const value of tags) {
		filters.appendChild(
			el(
				"a",
				{
					class: `mb-filter${tag === value ? " is-active" : ""}`,
					href: ctx.href("knowledge", undefined, { tag: value }),
				},
				value,
			),
		);
	}

	return el(
		"div",
		{ class: "mb-view mb-view--knowledge" },
		el(
			"section",
			{ class: "mb-section mb-section--white" },
			el(
				"div",
				{ class: "mb-section__inner" },
				breadcrumbs(ctx, [{ label: copy.nav.start, key: "home" }, { label: copy.nav.knowledge }]),
				el("h1", { class: "mb-pagehead__title" }, copy.knowledge.title),
				el("p", { class: "mb-pagehead__lead" }, copy.knowledge.lead),
				filters,
				grid,
				newsletterStrip(ctx),
			),
		),
	);
}

export async function articleDetailView(ctx: AppContext, match: RouteMatch): Promise<HTMLElement> {
	const copy = ctx.copy();
	const article = await ctx.gateway.article(match.params.slug ?? "");
	if (!article) return notFoundView(ctx);

	applyPageSeo(ctx, {
		title: `${article.title} — ${copy.brand.name}`,
		description: article.excerpt,
	});

	const tag = article.tags[0];
	const body = el("div", { class: "mb-prose mb-prose--article" });
	// CMS-authored rich text: trusted, server-owned content.
	clear(body);
	body.appendChild(fromTrustedHtml(article.body));

	const readingLabel =
		ctx.locale() === "en"
			? `${article.readingMinutes} min read`
			: `${article.readingMinutes} min czytania`;

	return el(
		"div",
		{ class: "mb-view mb-view--article" },
		el(
			"section",
			{ class: "mb-section mb-section--white" },
			el(
				"article",
				{ class: "mb-section__inner mb-article" },
				breadcrumbs(ctx, [
					{ label: copy.nav.start, key: "home" },
					{ label: copy.nav.knowledge, key: "knowledge" },
					{ label: tag ?? article.title },
				]),
				tag ? eyebrow(tag) : null,
				el("h1", { class: "mb-article__title" }, article.title),
				el("p", { class: "mb-article__lead" }, article.excerpt),
				el("div", { class: "mb-article__meta" }, pill(readingLabel, "neutral")),
				article.heroImage
					? el("img", {
							class: "mb-photo mb-photo--wide",
							src: article.heroImage.url,
							alt: article.heroImage.alt ?? "",
							loading: "lazy",
							decoding: "async",
						})
					: null,
				body,
				el(
					"div",
					{ class: "mb-endcta" },
					el("h2", { class: "mb-endcta__title" }, copy.article.ctaTitle),
					el("p", { class: "mb-endcta__desc" }, copy.article.ctaDesc),
					el(
						"div",
						{ class: "mb-endcta__actions" },
						el(
							"a",
							{
								class: "mb-cta mb-cta--accent",
								href: ctx.href("contact"),
								"data-routekey": "contact",
							},
							copy.article.ctaConsult,
						),
						el(
							"a",
							{
								class: "mb-cta mb-cta--outline",
								href: ctx.href("knowledge"),
								"data-routekey": "knowledge",
							},
							copy.article.ctaBack,
						),
					),
				),
			),
		),
	);
}
